import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
  createEoaDraft,
  finalizeEoa,
  getEoa,
  getEoaAcademicConfiguration,
  getEoaGradingConfiguration,
  getCollegeEligibilityFrameworks,
  getConfiguredSchools,
  getSchoolsConfig,
  configureSchool,
  patchEoaSchool,
  deleteConfiguredSchool,
  getEoaInfoConfiguration,
  getEoaSurveyConfiguration,
  patchEoa,
  patchEoaCollegeEligibility,
  patchEoaAcademicConfiguration,
  patchEoaGradingConfiguration,
  patchEoaInfoConfiguration,
  patchEoaSurveyConfiguration,
  saveEoaAcademicConfiguration,
  saveEoaCollegeEligibility,
  saveEoaGradingConfiguration,
  saveEoaInfoConfiguration,
  saveEoaSurveyConfiguration,
  getSurveyLanguages,
  SurveyLanguageOption,
} from '../../services/eoaConfigServices';
import useErrorModal from '../hooks/useErrorModal';
import useUserDetails from '../hooks/useUserDetails';
import { configurationPath } from './eoaConfigurationPaths';
import { ConfigurationStepId } from './eoaConfigurationSteps';
import { readOptionalResource } from './persistNestedConfiguration';
import { AcademicsForm, INITIAL_ACADEMICS_FORM, academicsFromEoa, persistAcademics } from './academicsForm';
import {
  DistrictInformationForm,
  INITIAL_DISTRICT_INFORMATION_FORM,
  districtInformationFromEoa,
  persistDistrictInformation,
} from './districtInformationForm';
import { GradingForm, INITIAL_GRADING_FORM, gradingFromEoa, persistGrading } from './gradingForm';
import {
  EoaaInfoForm,
  INITIAL_EOAA_INFO_FORM,
  eoaaInfoFromEoa,
  persistEoaaInfo,
  persistSurveyConfig,
  surveyConfigFromEoa,
  surveyConfigRecordExists,
} from './eoaaInfoForm';
import {
  EoaaInfo2Form,
  INITIAL_EOAA_INFO_2_FORM,
  eoaaInfo2FromFrameworks,
  persistCollegeEligibility,
} from './eoaaInfo2Form';
import {
  INITIAL_SCHOOL_INFO_FORM,
  SchoolInfoForm,
  hasInvalidSchoolInfo,
  persistSchoolInfo,
  schoolInfoFromSources,
} from './schoolInfoForm';

const AUTOSAVE_DELAY_MS = 600;

const useTrackedForm = <T>(initial: T) => {
  const [value, setValue] = useState(initial);
  const valueRef = useRef(value);
  const lastSavedRef = useRef(initial);
  valueRef.current = value;
  return { value, setValue, valueRef, lastSavedRef };
};

const useConfigurationDraft = (eoaId: string | undefined, stepId: ConfigurationStepId | undefined) => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const district = useTrackedForm(INITIAL_DISTRICT_INFORMATION_FORM);
  const academics = useTrackedForm(INITIAL_ACADEMICS_FORM);
  const grading = useTrackedForm(INITIAL_GRADING_FORM);
  const eoaaInfo = useTrackedForm(INITIAL_EOAA_INFO_FORM);
  const eoaaInfo2 = useTrackedForm(INITIAL_EOAA_INFO_2_FORM);
  const schoolInfo = useTrackedForm(INITIAL_SCHOOL_INFO_FORM);
  const [isHydrated, setIsHydrated] = useState(!eoaId);
  const [surveyLanguages, setSurveyLanguages] = useState<SurveyLanguageOption[]>([]);
  const surveyLanguagesRef = useRef(surveyLanguages);
  surveyLanguagesRef.current = surveyLanguages;
  const eoaIdRef = useRef(eoaId ?? null);
  const saveChainRef = useRef(Promise.resolve<string | null>(eoaId ?? null));
  const skipHydrateRef = useRef(false);
  const academicsExistsRef = useRef(false);
  const gradingExistsRef = useRef(false);
  const eoaaInfoExistsRef = useRef(false);
  const surveyConfigExistsRef = useRef(false);
  const persistAllRef = useRef<() => Promise<string | null>>(async () => null);

  useEffect(() => {
    if (eoaId) eoaIdRef.current = eoaId;
  }, [eoaId]);

  const enqueueSave = (task: (currentId: string | null) => Promise<string | null>): Promise<string | null> => {
    const pending = saveChainRef.current.then(task);
    saveChainRef.current = pending.catch((error) => {
      handleOpenErrorModal('Error occurred while saving EOAA configuration!', error as AxiosError);
      return eoaIdRef.current;
    });
    return saveChainRef.current;
  };

  const attachDraftToUrl = (savedId: string) => {
    if (savedId === eoaIdRef.current) return;
    eoaIdRef.current = savedId;
    if (!stepId) return;
    skipHydrateRef.current = true;
    navigate(configurationPath(savedId, stepId), { replace: true });
  };

  const saveDistrictInformation = async (
    nextForm: DistrictInformationForm,
    createIfEmpty: boolean
  ): Promise<string | null> => {
    if (!userDetails?.access_token) return eoaIdRef.current;

    const accessToken = userDetails.access_token;
    const savedId = await persistDistrictInformation({
      form: nextForm,
      previousForm: district.lastSavedRef.current,
      eoaId: eoaIdRef.current,
      createIfEmpty,
      createEoa: async (payload) => (await createEoaDraft(accessToken, payload)).data,
      patchEoa: (id, payload) => patchEoa(id, accessToken, payload),
    });
    if (!savedId) return eoaIdRef.current;
    district.lastSavedRef.current = nextForm;
    attachDraftToUrl(savedId);
    return savedId;
  };

  const persistDistrict = (nextForm: DistrictInformationForm): Promise<string | null> => {
    if (!userDetails?.access_token) return Promise.resolve(eoaIdRef.current);

    return enqueueSave(() => saveDistrictInformation(nextForm, false));
  };

  const persistNestedDraft = <TForm>(
    nextForm: TForm,
    lastSavedRef: MutableRefObject<TForm>,
    existsRef: MutableRefObject<boolean>,
    persist: (args: {
      form: TForm;
      previousForm: TForm;
      eoaId: string | null;
      exists: boolean;
      createDraft: () => Promise<string | null>;
    }) => Promise<boolean>
  ): Promise<string | null> => {
    if (!userDetails?.access_token) return Promise.resolve(eoaIdRef.current);

    return enqueueSave(async (currentId) => {
      existsRef.current = await persist({
        form: nextForm,
        previousForm: lastSavedRef.current,
        eoaId: eoaIdRef.current ?? currentId,
        exists: existsRef.current,
        createDraft: () => saveDistrictInformation(district.valueRef.current, true),
      });
      const eoaIdToSave = eoaIdRef.current ?? currentId;
      if (eoaIdToSave) lastSavedRef.current = nextForm;
      return eoaIdToSave;
    });
  };

  const persistAcademicsDraft = (nextForm: AcademicsForm): Promise<string | null> => {
    const accessToken = userDetails?.access_token;
    if (!accessToken) return Promise.resolve(eoaIdRef.current);

    return persistNestedDraft(nextForm, academics.lastSavedRef, academicsExistsRef, (args) =>
      persistAcademics({
        ...args,
        createAcademics: (id, payload) => saveEoaAcademicConfiguration(id, accessToken, payload),
        patchAcademics: (id, payload) => patchEoaAcademicConfiguration(id, accessToken, payload),
      })
    );
  };

  const persistGradingDraft = (nextForm: GradingForm): Promise<string | null> => {
    const accessToken = userDetails?.access_token;
    if (!accessToken) return Promise.resolve(eoaIdRef.current);

    return persistNestedDraft(nextForm, grading.lastSavedRef, gradingExistsRef, (args) =>
      persistGrading({
        ...args,
        createGrading: (id, payload) => saveEoaGradingConfiguration(id, accessToken, payload),
        patchGrading: (id, payload) => patchEoaGradingConfiguration(id, accessToken, payload),
      })
    );
  };

  const persistEoaaInfoDraft = (nextForm: EoaaInfoForm): Promise<string | null> => {
    if (!userDetails?.access_token) return Promise.resolve(eoaIdRef.current);

    const accessToken = userDetails.access_token;
    return enqueueSave(async (currentId) => {
      const createDraft = () => saveDistrictInformation(district.valueRef.current, true);
      let eoaIdToSave = eoaIdRef.current ?? currentId;
      if (!eoaIdToSave && nextForm.yearEnding.trim()) {
        eoaIdToSave = await createDraft();
      }
      if (eoaIdToSave && eoaaInfo.lastSavedRef.current.yearEnding !== nextForm.yearEnding) {
        const yearEnding = nextForm.yearEnding.trim();
        await patchEoa(eoaIdToSave, accessToken, { year_ending: yearEnding ? Number(yearEnding) : null });
      }
      eoaaInfoExistsRef.current = await persistEoaaInfo({
        form: nextForm,
        previousForm: eoaaInfo.lastSavedRef.current,
        eoaId: eoaIdToSave,
        exists: eoaaInfoExistsRef.current,
        createEoaaInfo: (id, payload) => saveEoaInfoConfiguration(id, accessToken, payload),
        patchEoaaInfo: (id, payload) => patchEoaInfoConfiguration(id, accessToken, payload),
        createDraft,
      });
      surveyConfigExistsRef.current = await persistSurveyConfig({
        form: nextForm,
        previousForm: eoaaInfo.lastSavedRef.current,
        eoaId: eoaIdToSave,
        exists: surveyConfigExistsRef.current,
        languages: surveyLanguagesRef.current,
        createSurveyConfig: (id, payload) => saveEoaSurveyConfiguration(id, accessToken, payload),
        patchSurveyConfig: (id, payload) => patchEoaSurveyConfiguration(id, accessToken, payload),
        createDraft,
      });
      eoaIdToSave = eoaIdRef.current ?? eoaIdToSave;
      if (eoaIdToSave) {
        eoaaInfo.lastSavedRef.current = nextForm;
      }
      return eoaIdToSave;
    });
  };

  const persistEoaaInfo2Draft = (nextForm: EoaaInfo2Form): Promise<string | null> => {
    if (!userDetails?.access_token) return Promise.resolve(eoaIdRef.current);

    const accessToken = userDetails.access_token;
    return enqueueSave(async (currentId) => {
      const saved = await persistCollegeEligibility({
        form: nextForm,
        previousForm: eoaaInfo2.lastSavedRef.current,
        eoaId: eoaIdRef.current ?? currentId,
        createCollegeEligibility: async (id, payload) =>
          (await saveEoaCollegeEligibility(id, accessToken, payload)).data,
        patchCollegeEligibility: (id, frameworkId, payload) =>
          patchEoaCollegeEligibility(id, frameworkId, accessToken, payload),
        createDraft: () => saveDistrictInformation(district.valueRef.current, true),
      });
      const eoaIdToSave = eoaIdRef.current ?? currentId;
      if (eoaIdToSave) {
        eoaaInfo2.lastSavedRef.current = saved;
        eoaaInfo2.setValue((current) => {
          const unchanged = Object.entries(saved.frameworkIds).every(
            ([key, id]) => current.frameworkIds[key as keyof typeof current.frameworkIds] === id
          );
          return unchanged
            ? current
            : {
                ...current,
                frameworkIds: { ...current.frameworkIds, ...saved.frameworkIds },
              };
        });
      }
      return eoaIdToSave;
    });
  };

  const persistSchoolInfoDraft = (nextForm: SchoolInfoForm): Promise<string | null> => {
    if (!userDetails?.access_token) return Promise.resolve(eoaIdRef.current);

    const accessToken = userDetails.access_token;
    return enqueueSave(async (currentId) => {
      if (hasInvalidSchoolInfo(nextForm)) return eoaIdRef.current ?? currentId;
      const saved = await persistSchoolInfo({
        form: nextForm,
        previousForm: schoolInfo.lastSavedRef.current,
        eoaId: eoaIdRef.current ?? currentId,
        createSchool: async (id, payload) => (await configureSchool(id, accessToken, payload)).data,
        patchSchool: (id, schoolId, payload) => patchEoaSchool(id, schoolId, accessToken, payload),
        deleteSchool: (id, schoolId) => deleteConfiguredSchool(id, accessToken, schoolId),
        createDraft: () => saveDistrictInformation(district.valueRef.current, true),
      });
      const eoaIdToSave = eoaIdRef.current ?? currentId;
      if (eoaIdToSave) {
        schoolInfo.lastSavedRef.current = saved;
        schoolInfo.setValue((current) => {
          let changed = false;
          const schools = current.schools.map((school) => {
            const persisted = saved.schools.find((row) => row.id === school.id);
            if (!persisted || persisted.savedId === school.savedId) return school;
            changed = true;
            return { ...school, savedId: persisted.savedId };
          });
          return changed ? { ...current, schools } : current;
        });
      }
      return eoaIdToSave;
    });
  };

  const persistAll = async (): Promise<string | null> => {
    await persistDistrict(district.valueRef.current);
    await persistAcademicsDraft(academics.valueRef.current);
    await persistGradingDraft(grading.valueRef.current);
    await persistEoaaInfoDraft(eoaaInfo.valueRef.current);
    await persistEoaaInfo2Draft(eoaaInfo2.valueRef.current);
    await persistSchoolInfoDraft(schoolInfo.valueRef.current);
    return eoaIdRef.current;
  };
  persistAllRef.current = persistAll;

  const finalizeDraft = async (): Promise<string | null> => {
    if (!userDetails?.access_token) return null;

    const savedId = await persistAll();
    if (!savedId) return null;

    try {
      await finalizeEoa(savedId, userDetails.access_token);
      return savedId;
    } catch (error) {
      handleOpenErrorModal('Error occurred while submitting EOAA configuration!', error as AxiosError);
      return null;
    }
  };

  useEffect(() => {
    const loadDraft = async () => {
      if (!eoaId) {
        if (!eoaIdRef.current) {
          academicsExistsRef.current = false;
          gradingExistsRef.current = false;
          eoaaInfoExistsRef.current = false;
          surveyConfigExistsRef.current = false;
        }
        setIsHydrated(true);
        return;
      }
      if (skipHydrateRef.current) {
        skipHydrateRef.current = false;
        district.lastSavedRef.current = district.valueRef.current;
        academics.lastSavedRef.current = academics.valueRef.current;
        grading.lastSavedRef.current = grading.valueRef.current;
        eoaaInfo.lastSavedRef.current = eoaaInfo.valueRef.current;
        eoaaInfo2.lastSavedRef.current = eoaaInfo2.valueRef.current;
        schoolInfo.lastSavedRef.current = schoolInfo.valueRef.current;
        setIsHydrated(true);
        return;
      }
      if (!userDetails?.access_token) return;

      setIsHydrated(false);
      try {
        const accessToken = userDetails.access_token;
        const [
          eoaResponse,
          academicsData,
          gradingData,
          eoaaInfoData,
          surveyConfigData,
          collegeEligibilityData,
          configuredSchools,
          schoolsConfig,
        ] = await Promise.all([
          getEoa(eoaId, accessToken),
          readOptionalResource(getEoaAcademicConfiguration(eoaId, accessToken)),
          readOptionalResource(getEoaGradingConfiguration(eoaId, accessToken)),
          readOptionalResource(getEoaInfoConfiguration(eoaId, accessToken)),
          readOptionalResource(getEoaSurveyConfiguration(eoaId, accessToken)),
          readOptionalResource(getCollegeEligibilityFrameworks(eoaId, accessToken)),
          readOptionalResource(getConfiguredSchools(eoaId, accessToken)),
          readOptionalResource(getSchoolsConfig(eoaId, accessToken)),
        ]);
        const loadedDistrict = districtInformationFromEoa(eoaResponse.data);
        const loadedAcademics = academicsFromEoa(academicsData ?? {});
        const loadedGrading = gradingFromEoa(gradingData ?? {});
        const loadedEoaaInfo = {
          ...eoaaInfoFromEoa(eoaaInfoData ?? {}, eoaResponse.data.year_ending),
          ...surveyConfigFromEoa(surveyConfigData ?? {}),
        };
        const loadedEoaaInfo2 = eoaaInfo2FromFrameworks(collegeEligibilityData?.frameworks ?? []);
        const loadedSchoolInfo = schoolInfoFromSources(configuredSchools ?? [], schoolsConfig ?? []);
        district.setValue(loadedDistrict);
        academics.setValue(loadedAcademics);
        grading.setValue(loadedGrading);
        eoaaInfo.setValue(loadedEoaaInfo);
        eoaaInfo2.setValue(loadedEoaaInfo2);
        schoolInfo.setValue(loadedSchoolInfo);
        district.lastSavedRef.current = loadedDistrict;
        academics.lastSavedRef.current = loadedAcademics;
        grading.lastSavedRef.current = loadedGrading;
        eoaaInfo.lastSavedRef.current = loadedEoaaInfo;
        eoaaInfo2.lastSavedRef.current = loadedEoaaInfo2;
        schoolInfo.lastSavedRef.current = loadedSchoolInfo;
        academicsExistsRef.current = academicsData !== null;
        gradingExistsRef.current = gradingData !== null;
        eoaaInfoExistsRef.current = eoaaInfoData !== null;
        surveyConfigExistsRef.current = surveyConfigRecordExists(surveyConfigData);
      } catch (error) {
        handleOpenErrorModal('Error occurred while loading EOAA configuration!', error as AxiosError);
      } finally {
        setIsHydrated(true);
      }
    };

    void loadDraft();
  }, [eoaId, userDetails?.access_token, handleOpenErrorModal]);

  useEffect(() => {
    const loadLanguages = async () => {
      if (!eoaId || !userDetails?.access_token) {
        setSurveyLanguages([]);
        return;
      }
      try {
        const response = await getSurveyLanguages(eoaId, userDetails.access_token);
        setSurveyLanguages(response.data);
      } catch {
        setSurveyLanguages([]);
      }
    };

    void loadLanguages();
  }, [eoaId, userDetails?.access_token]);

  const persistActiveStep = () => {
    if (stepId === 'district-information') return persistDistrict(district.valueRef.current);
    if (stepId === 'academics') return persistAcademicsDraft(academics.valueRef.current);
    if (stepId === 'grading') return persistGradingDraft(grading.valueRef.current);
    if (stepId === 'eoaa-info-1') return persistEoaaInfoDraft(eoaaInfo.valueRef.current);
    if (stepId === 'eoaa-info-2') return persistEoaaInfo2Draft(eoaaInfo2.valueRef.current);
    if (stepId === 'school-info') return persistSchoolInfoDraft(schoolInfo.valueRef.current);
    return undefined;
  };

  const activeForm =
    stepId === 'district-information'
      ? district.value
      : stepId === 'academics'
        ? academics.value
        : stepId === 'grading'
          ? grading.value
          : stepId === 'eoaa-info-1'
            ? eoaaInfo.value
            : stepId === 'eoaa-info-2'
              ? eoaaInfo2.value
              : stepId === 'school-info'
                ? schoolInfo.value
                : null;

  useEffect(() => {
    if (!isHydrated || activeForm === null) return;

    const timeoutId = window.setTimeout(() => {
      void persistActiveStep();
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [activeForm, isHydrated, stepId, eoaId, userDetails?.access_token]);

  useEffect(() => {
    return () => {
      void persistAllRef.current();
    };
  }, [eoaId, userDetails?.access_token]);

  return {
    districtInformation: district.value,
    setDistrictInformation: district.setValue,
    academics: academics.value,
    setAcademics: academics.setValue,
    grading: grading.value,
    setGrading: grading.setValue,
    eoaaInfo: eoaaInfo.value,
    setEoaaInfo: eoaaInfo.setValue,
    eoaaInfo2: eoaaInfo2.value,
    setEoaaInfo2: eoaaInfo2.setValue,
    schoolInfo: schoolInfo.value,
    setSchoolInfo: schoolInfo.setValue,
    surveyLanguages,
    isHydrated,
    persistDraft: persistAll,
    finalizeDraft,
  };
};

export default useConfigurationDraft;
